import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useTaskEmails } from '@/hooks/useTaskEmails';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Mail, Clock, Paperclip } from 'lucide-react';
import { format } from 'date-fns';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { AttachmentList } from '@/components/email/AttachmentList';

interface TaskEmailsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  taskId: string;
}

export function TaskEmailsDialog({ open, onOpenChange, taskId }: TaskEmailsDialogProps) {
  const { data: emails, isLoading } = useTaskEmails(open ? taskId : null);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Linked Emails ({emails?.length || 0})
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-2">
          {isLoading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="space-y-2">
                  <Skeleton className="h-6 w-3/4" />
                  <Skeleton className="h-4 w-1/2" />
                  <Skeleton className="h-16 w-full" />
                </div>
              ))}
            </div>
          ) : emails && emails.length > 0 ? (
            <Accordion type="single" collapsible className="space-y-3">
              {emails.map((email, index) => (
                <AccordionItem
                  key={email.id}
                  value={email.id}
                  className="border rounded-lg px-4"
                >
                  <AccordionTrigger className="hover:no-underline py-3">
                    <div className="flex flex-col items-start gap-2 text-left flex-1 pr-2">
                      <div className="flex items-center gap-2 w-full">
                        <h3 className="font-semibold text-sm flex-1">
                          {email.subject || '(No subject)'}
                        </h3>
                        {!email.is_read && (
                          <Badge variant="secondary" className="text-xs">
                            Unread
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground w-full">
                        <span className="font-medium">
                          {email.from_name || email.from_address}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {format(new Date(email.received_at), 'MMM d, yyyy h:mm a')}
                        </span>
                        {email.has_attachments && (
                          <span className="flex items-center gap-1">
                            <Paperclip className="h-3 w-3" />
                            Attachments
                          </span>
                        )}
                      </div>
                      {email.body_text && (
                        <p className="text-xs text-muted-foreground line-clamp-2">
                          {email.body_text.substring(0, 150)}
                          {email.body_text.length > 150 ? '...' : ''}
                        </p>
                      )}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pb-3 pt-1 space-y-3">
                    <div className="bg-muted/50 rounded-md p-3 text-sm whitespace-pre-wrap max-h-60 overflow-y-auto">
                      {email.body_text || 'No content available'}
                    </div>
                    
                    {email.has_attachments && (
                      <div className="pt-2 border-t">
                        <AttachmentList
                          emailId={email.id}
                          emailAccountId={email.email_account_id}
                          messageId={email.message_id}
                        />
                      </div>
                    )}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Mail className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No linked emails found</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
